import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentTenant, type TenantInfo } from '../utils/tenant';

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  domain?: string;
}

interface TenantContextType extends TenantInfo {
  tenant: Tenant | null;
  setTenant: (tenantId: string, domain?: string) => void;
  clearTenant: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant] = useState<Tenant | null>(null); // Will be populated from GraphQL later
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantDomain, setTenantDomain] = useState<string | null>(null);
  const [isPlatform, setIsPlatform] = useState<boolean>(true);

  useEffect(() => {
    // Detect tenant from URL/hostname
    const tenantInfo = getCurrentTenant();

    setTenantId(tenantInfo.tenantId);
    setTenantDomain(tenantInfo.tenantDomain);
    setIsPlatform(tenantInfo.isPlatform);
  }, []);

  const setTenant = (newTenantId: string, domain?: string) => {
    setTenantId(newTenantId);
    setTenantDomain(domain || null);
    setIsPlatform(!newTenantId);
  };

  const clearTenant = () => {
    setTenantId(null);
    setTenantDomain(null);
    setIsPlatform(true);
  };

  return (
    <TenantContext.Provider
      value={{
        tenant,
        tenantId,
        tenantDomain,
        isPlatform,
        setTenant,
        clearTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
